/**
 * TemplateSectionsList Component
 *
 * List container for template sections with drag-and-drop reordering using @dnd-kit.
 * Handles empty state and includes "Add Section" cards between items.
 * Follows best practices from the notes/template-editor feature.
 */

import React, { useState } from 'react';
import { BetweenItemAddButton } from '@components/ui/buttons/BetweenItemAddButton';
import {
  closestCenter,
  DndContext,
  type DragEndEvent,
  DragOverlay,
  type DragStartEvent,
  MeasuringStrategy,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { Box, Paper, Typography } from '@mui/material';

import type { SectionWithMeta } from '../types';

import { AddSectionCard } from './AddSectionCard';
import { TemplateSectionItem } from './TemplateSectionItem';

interface TemplateSectionsListProps {
  /** List of sections with metadata */
  sections: SectionWithMeta[];
  /** Whether sections can be modified */
  canModify: boolean;
  /** Callback when sections are reordered */
  onReorderSections: (startIndex: number, endIndex: number) => void;
  /** Callback to remove a section */
  onRemoveSection: (moduleId: string) => void;
  /** Callback to revert a section's customization */
  onRevertCustomization: (moduleId: string) => void;
  /** Callback to add section at index */
  onAddSectionAt: (index: number) => void;
  /** Whether module selector is open (hide add buttons) */
  isAddingSections: boolean;
}

export const TemplateSectionsList: React.FC<TemplateSectionsListProps> = ({
  sections,
  canModify,
  onReorderSections,
  onRemoveSection,
  onRevertCustomization,
  onAddSectionAt,
  isAddingSections,
}) => {
  const [activeId, setActiveId] = useState<string | null>(null);

  // Configure drag sensors - require 8px drag before activating
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8,
      },
    }),
  );

  const handleDragStart = (event: DragStartEvent) => {
    setActiveId(event.active.id as string);
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveId(null);

    if (!over || active.id === over.id) return;

    const oldIndex = sections.findIndex(s => s.module_id === active.id);
    const newIndex = sections.findIndex(s => s.module_id === over.id);

    if (oldIndex !== -1 && newIndex !== -1) {
      onReorderSections(oldIndex, newIndex);
    }
  };

  const activeSection = activeId ? sections.find(s => s.module_id === activeId) : null;

  // Empty state - show single "Add Section" card
  if (sections.length === 0) {
    return (
      <Box sx={{ py: 4 }}>
        {canModify ? (
          <AddSectionCard onClick={() => onAddSectionAt(0)} />
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              py: 8,
              textAlign: 'center',
            }}
          >
            <Typography variant="body1" color="text.secondary">
              This template has no sections.
            </Typography>
          </Box>
        )}
      </Box>
    );
  }

  // Render with drag-and-drop if modifiable
  if (canModify) {
    return (
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
        measuring={{
          droppable: {
            strategy: MeasuringStrategy.BeforeDragging,
          },
        }}
      >
        <SortableContext
          items={sections.map(s => s.module_id)}
          strategy={verticalListSortingStrategy}
        >
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 0,
            }}
          >
            {/* Add section button at the top */}
            {!isAddingSections && (
              <BetweenItemAddButton
                enabled={activeId === null}
                onClick={() => onAddSectionAt(0)}
                label="Section"
              />
            )}

            {sections.map((section, index) => (
              <React.Fragment key={section.module_id}>
                <TemplateSectionItem
                  section={section}
                  canModify={canModify}
                  onRemove={() => onRemoveSection(section.module_id)}
                  onRevertCustomization={
                    section.isCustomized
                      ? () => onRevertCustomization(section.module_id)
                      : undefined
                  }
                />
                {/* Add section button after each item */}
                {!isAddingSections && (
                  <BetweenItemAddButton
                    enabled={activeId === null}
                    onClick={() => onAddSectionAt(index + 1)}
                    label="Section"
                  />
                )}
              </React.Fragment>
            ))}
          </Box>
        </SortableContext>

        {/* DragOverlay for smooth dragging experience */}
        <DragOverlay
          dropAnimation={
            activeSection
              ? null // No animation on successful drop to prevent flash
              : {
                  duration: 500,
                  easing: 'cubic-bezier(0.18, 0.67, 0.6, 1.22)',
                }
          }
        >
          {activeSection && (
            <Paper
              elevation={8}
              sx={{
                p: 2,
                borderRadius: 2,
                cursor: 'grabbing',
                opacity: 0.9,
                border: 2,
                borderColor: 'primary.main',
                bgcolor: 'background.paper',
              }}
            >
              <Typography variant="subtitle2" fontWeight={600}>
                {activeSection.display_name}
              </Typography>
              {(activeSection.moduleInfo?.description ||
                activeSection.moduleInfo?.public_description) && (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontSize: '0.8125rem',
                    mt: 0.5,
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {activeSection.moduleInfo?.description ||
                    activeSection.moduleInfo?.public_description}
                </Typography>
              )}
            </Paper>
          )}
        </DragOverlay>
      </DndContext>
    );
  }

  // Read-only view (no drag-and-drop)
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1.5,
      }}
    >
      {sections.map(section => (
        <TemplateSectionItem
          key={section.module_id}
          section={section}
          canModify={false}
        />
      ))}
    </Box>
  );
};
